import { Injectable } from '@angular/core';
import { Observable, Subject, map, tap } from 'rxjs';

// Services
import { RouteParam, ServiceBase } from '../service-base';

// Interfaces
import { SaveOptions } from '../../interfaces/save-options';

// Modèles
import { ModelBase } from 'src/app/@shared/models/abstract/model-base.model';

// Utils
import { ODataError } from 'src/app/@shared/utilities/odata/response';

const CLASS_NAME = 'AgentMarker';
const ENDPOINT = '/agent/markers/';
const ROUTES: RouteParam[] = [
    { key: 'keep', path: '{id}/keep/' },
    { key: 'undo', path: '{id}/undo/' },
];

/** Le marqueur n'existe plus : l'objet a déjà été revu, conservé ou annulé. */
export const AGENT_MARKER_NOT_FOUND = 'AGENT_MARKER_NOT_FOUND';

/**
 * Annuler ou conserver ce que l'agent IA a écrit sur un objet (#1217).
 *
 * Les deux routes agissent sur le **marqueur**, corps ignoré. Le message de succès est
 * relayé ; un refus métier (400) ou un droit manquant (403) s'affichent comme partout.
 * Le 404 « marqueur introuvable » veut dire « déjà revu » — un humain est passé entre-temps :
 * ce n'est pas une erreur à afficher, l'appelant rafraîchit ce qu'il montre.
 *
 * `reviewed$` prévient les listes qui affichent l'étincelle : un marqueur vient d'être
 * conservé, annulé, ou se révèle déjà revu. C'est leur seul signal quand le geste a lieu
 * dans une fenêtre ouverte depuis elles — l'annulation ne passe pas par le service métier.
 */
@Injectable({
    providedIn: 'root'
})
export class AgentMarkerService extends ServiceBase {

    private _reviewed$ = new Subject<void>();
    /** Un marqueur vient d'être conservé, annulé, ou trouvé déjà revu. */
    public readonly reviewed$ = this._reviewed$.asObservable();

    constructor() {
        super(CLASS_NAME, ENDPOINT);
        this.addRoutes(ROUTES);
    }

    /** Conserver : le marqueur s'efface, l'objet reste. */
    public keep$(markerId: number): Observable<void> {
        return this._putAction$(this.routes.keep.replace('{id}', markerId.toString())).pipe(
            tap(() => this._reviewed$.next()),
            map(() => undefined)
        );
    }

    /**
     * Annuler : l'objet revient à son état d'avant l'agent — une création est supprimée,
     * une transition est défaite par la transition inverse. La valeur renvoyée (l'objet
     * après annulation, ou `null`) est ignorée : l'appelant relit ce qu'il affiche.
     */
    public undo$(markerId: number): Observable<void> {
        return this._putAction$(this.routes.undo.replace('{id}', markerId.toString())).pipe(
            tap(() => this._reviewed$.next()),
            map(() => undefined)
        );
    }

    /** « Déjà revu » ne s'affiche pas, mais se signale ; l'erreur est tout de même propagée à l'appelant. */
    protected override handleError(e: any, instance?: ModelBase, options?: SaveOptions) {
        const error = ODataError.fromResponse(e, instance);
        if (error.messages.some(message => message.code === AGENT_MARKER_NOT_FOUND)) {
            this._reviewed$.next();
            throw error;
        }
        super.handleError(e, instance, options);
    }
}
